import React, {useMemo} from 'react';
import {SingleValue} from 'react-select';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Input} from 'semantic-ui-react';

import {
  sortTemplateVariablesByConfig,
  VariableFormState,
} from './defaultResourceConfigUtil';
import {SelectVariableType, VariableTypeOption} from './SelectVariableType';
import * as S from './TemplateVariableForm.styles';

interface DefineResourceTemplateVariablesFormProps {
  initialState: Record<string, VariableFormState>;
  rawConfigStr: string;
  onUpdateVariable: (
    variableName: string,
    variableState: VariableFormState
  ) => void;
}

export const DefineResourceTemplateVariablesForm = ({
  initialState,
  rawConfigStr,
  onUpdateVariable,
}: DefineResourceTemplateVariablesFormProps) => {
  const sortedVars = useMemo(() => {
    const vars = Object.keys(initialState).map(key => initialState[key]);
    return sortTemplateVariablesByConfig(rawConfigStr, vars);
  }, [initialState, rawConfigStr]);

  return (
    <>
      {sortedVars.map(templateVar => {
        return (
          <VariableForm
            initialState={templateVar}
            key={templateVar.name}
            onUpdateVariable={onUpdateVariable}
          />
        );
      })}
    </>
  );
};

interface VariableFormProps {
  initialState: VariableFormState;
  onUpdateVariable: (
    variableName: string,
    variableState: VariableFormState
  ) => void;
}

export const VariableForm = ({
  initialState,
  onUpdateVariable,
}: VariableFormProps) => {
  const [description, setDescription] = React.useState(
    initialState.description
  );
  const [variableType, setVariableType] = React.useState(initialState?.type);
  const [valuesStr, setValuesStr] = React.useState(initialState?.values);
  const [defaultValue, setDefaultValue] = React.useState(initialState?.default);
  const [minimum, setMin] = React.useState(initialState?.minimum);
  const [maximum, setMax] = React.useState(initialState?.maximum);
  const name = initialState.name;

  const onDescriptionChange = (value: string) => {
    setDescription(value);
  };

  const onTypeChange = (value: SingleValue<VariableTypeOption>) => {
    setVariableType(value?.value);
  };

  const onMinChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (variableType === 'integer') {
        setMin(parseInt(event.target.value, 10));
      } else {
        setMin(parseFloat(event.target.value));
      }
    } catch (e) {
      setMin(undefined);
    }
  };

  const onMaxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (variableType === 'integer') {
        setMax(parseInt(event.target.value, 10));
      } else {
        setMax(parseFloat(event.target.value));
      }
    } catch (e) {
      setMax(undefined);
    }
  };

  // set variable type when the form is first loaded.
  React.useEffect(() => {
    if (initialState.type == null) {
      return;
    }
    switch (initialState.type.toLowerCase()) {
      case 'string':
        setVariableType('string');
        break;
      case 'integer':
        setVariableType('integer');
        break;
      case 'float':
        setVariableType('number');
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Right now this get run on every keystroke in a text field. This is probably
  // not ideal, but it works for now.
  React.useEffect(() => {
    onUpdateVariable(name, {
      description,
      type: variableType,
      values: valuesStr,
      minimum,
      maximum,
      default: defaultValue,
      name,
    });
  }, [
    description,
    variableType,
    valuesStr,
    minimum,
    maximum,
    defaultValue,
    name,
    onUpdateVariable,
  ]);

  const initialType = variableType
    ? {
        value: variableType,
        label: variableType.charAt(0).toUpperCase() + variableType.slice(1),
      }
    : undefined;

  const defaultPlaceHolder =
    variableType === 'string'
      ? 'ex. Option-1'
      : variableType === 'integer'
        ? 'ex. 50'
        : 'ex. 50.5';
  const minPlaceHolder = variableType === 'integer' ? 'ex. 1' : 'ex. 0.5';
  const maxPlaceHolder = variableType === 'integer' ? 'ex. 100' : 'ex. 99.5';
  const descriptionPlaceHolder =
    variableType === 'string'
      ? 'ex. Select option'
      : variableType === 'integer'
        ? 'ex. Seconds to retain the resource after completion'
        : 'ex. Seconds to retain the resource after completion';

  return (
    <S.TemplateVariableFormLayout>
      <S.VariableName>
        <S.VariableNameText>{name}</S.VariableNameText>
        <S.VariableNameFiller />
      </S.VariableName>
      <S.VariableFormField>Type</S.VariableFormField>
      <SelectVariableType
        selectedType={initialType}
        onSelectVariableType={onTypeChange}
      />
      {variableType === 'string' && (
        <>
          <S.VariableFormFieldRow>
            <S.VariableFormField>Select options</S.VariableFormField>
            <S.VariableFormFieldNote>
              (separated by commas) Optional
            </S.VariableFormFieldNote>
          </S.VariableFormFieldRow>
          <Input
            placeholder="option-1, option-2, option-3"
            value={valuesStr}
            size="small"
            onChange={event => setValuesStr(event?.target.value)}
          />
          <S.VariableFormFieldRow>
            <S.VariableFormField>Default value</S.VariableFormField>
            <S.VariableFormFieldNote>Optional</S.VariableFormFieldNote>
          </S.VariableFormFieldRow>
          <Input
            placeholder={defaultPlaceHolder}
            value={defaultValue}
            size="small"
            onChange={event => setDefaultValue(event.target.value)}
          />
        </>
      )}
      {(variableType === 'integer' || variableType === 'number') && (
        <>
          <S.MinMaxWrapper>
            <S.MinMaxInnerSeparator>
              <S.VariableFormFieldRow>
                <S.VariableFormField>Min</S.VariableFormField>
                <S.VariableFormFieldNote>Optional</S.VariableFormFieldNote>
              </S.VariableFormFieldRow>
              <Input
                type="number"
                placeholder={minPlaceHolder}
                onChange={onMinChange}
                size="small"
                value={minimum}
              />
            </S.MinMaxInnerSeparator>
            <S.MinMaxInnerSeparator>
              <S.VariableFormFieldRow>
                <S.VariableFormField>Max</S.VariableFormField>
                <S.VariableFormFieldNote>Optional</S.VariableFormFieldNote>
              </S.VariableFormFieldRow>
              <Input
                type="number"
                placeholder={maxPlaceHolder}
                onChange={onMaxChange}
                size="small"
                value={maximum}
              />
            </S.MinMaxInnerSeparator>
          </S.MinMaxWrapper>
          <S.VariableFormFieldRow>
            <S.VariableFormField>Default</S.VariableFormField>
            <S.VariableFormFieldNote>Optional</S.VariableFormFieldNote>
          </S.VariableFormFieldRow>
          <Input
            type="number"
            placeholder={defaultPlaceHolder}
            onChange={event => setDefaultValue(event.target.value)}
            size="small"
            value={defaultValue}
          />
        </>
      )}
      <S.VariableFormFieldRow>
        <S.VariableFormField>Description</S.VariableFormField>
        <S.VariableFormFieldNote>Optional</S.VariableFormFieldNote>
      </S.VariableFormFieldRow>
      <Input
        placeholder={descriptionPlaceHolder}
        value={description}
        onChange={event => onDescriptionChange(event.target.value)}
        size="small"
      />
    </S.TemplateVariableFormLayout>
  );
};
